import React, { useCallback, useEffect } from 'react';
import { Button, Input, Popover, Tag, useToasts } from 'freemium-ui';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { isTagValid } from 'routes/utils';
import Icon from 'shared/components/Icon';
import Spacer from 'shared/components/Spacer';
import { useGetAccountInfo } from 'shared/hooks';
import { SurveyResponseType } from 'shared/types';
import { useAccessControl } from 'utils/access-control';
import {
  useAddTagMutation,
  useAttachTagWithResponseMutation,
  useDeleteTagWithResponseMutation,
  useGetTagInfo,
  useGetTags,
} from '../../../../hooks';
import { MIN_TAG_LENGTH } from '../../../../constants';
import { MenuContainer, TagsContainer } from './style';

type TagType = {
  id?: any;
  name: string;
  deleted?: boolean;
};

type ResponseTagsType = {
  surveyId?: string | null | undefined;
  responseData?: SurveyResponseType;
  handleSubmit?: (tagId: string) => void;
  isBulkAction?: boolean;
  disabled?: boolean;
  isResponseTranslated?: boolean;
};

const ResponseTags = (props: ResponseTagsType) => {
  const { t } = useTranslation();

  const {
    surveyId,
    responseData,
    isBulkAction,
    handleSubmit,
    disabled = false,
    isResponseTranslated,
  } = props;
  const { surveyserv_response_id, tags } = responseData || {};
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [filteredTags, setFilteredTags] = React.useState<TagType[]>([]);
  const [tagName, setTagName] = React.useState('');
  const [, setToast] = useToasts();
  const { access } = useAccessControl();
  const {
    refetch: refetchTags,
    isLoading: isTagsLoading,
    isFetching: isTagsFetching,
  } = useGetTags({
    searchParam: tagName,
    callback: (isSuccess, res) => {
      if (isSuccess && res?.accountTags?.length) {
        setFilteredTags(res.accountTags);
      }
    },
  });

  const {
    data: accountInfo,
    isLoading: isLoadingAccountInfo,
    isFetching: isFetchingAccountInfo,
  } = useGetAccountInfo({});

  const {
    mutate: attachTagWithResponse,
    isLoading: isAttachTagWithResponseLoading,
  } = useAttachTagWithResponseMutation({
    setToast,
    callback: (isSuccess) => {
      if (isSuccess) {
        setFilteredTags([]);
        setTagName('');
      }
    },
  });

  const { mutate: deleteTag, isLoading: isDeleting } =
    useDeleteTagWithResponseMutation({
      setToast,
    });

  const { mutate: addTag, isLoading: isAddTagLoading } = useAddTagMutation({
    callback: (isSuccess: boolean, res: any) => {
      if (isSuccess && surveyId) {
        attachTagWithResponse({
          surveyId,
          responseId: surveyserv_response_id || '',
          tagId: res.id,
        });
      }
      // for bulk action handling
      if (isSuccess && isBulkAction && handleSubmit) {
        handleSubmit(res?.id);
      }
    },
  });

  const addSelectedTags = (tag: any) => {
    const { tags = [] } = responseData || {};
    if (isBulkAction && handleSubmit) {
      handleSubmit(tag.id);
    } else {
      if (!tags?.includes(tag.id) && surveyId) {
        attachTagWithResponse({
          surveyId,
          responseId: surveyserv_response_id || '',
          tagId: tag.id,
        });
      }
    }
    setFilteredTags([]);
    setTagName('');
  };

  const removeTag = (tagId: string) => {
    if (surveyId)
      deleteTag({
        surveyId,
        responseId: surveyserv_response_id || '',
        tagId,
      });
  };

  const createTag = (tagName: string) => {
    const isTagExists =
      filteredTags.find((item: TagType) => item.name === tagName) || false;
    if (isTagExists && !tags?.includes(isTagExists?.id) && surveyId) {
      if (isBulkAction && handleSubmit) {
        handleSubmit(isTagExists.id);
      } else {
        attachTagWithResponse({
          surveyId,
          responseId: surveyserv_response_id || '',
          tagId: isTagExists.id,
        });
      }
    } else {
      addTag({
        payload: {
          name: tagName,
        },
        responseId: surveyserv_response_id || '',
      });
    }
    setTagName('');
  };

  const debouncedSearch = useCallback(
    debounce(() => {
      setFilteredTags([]);
      refetchTags();
    }, 500),
    [],
  );

  return (
    <TagsContainer>
      {access?.includes('response-edit:write') ? (
        <div className="add-tag-container">
          <Button
            id="add-tag-btn"
            type="secondary"
            inline
            className="flex"
            size="mini"
            disabled={
              isAttachTagWithResponseLoading ||
              isAddTagLoading ||
              isTagsLoading ||
              isDeleting ||
              disabled
            }
            aria-describedby="tag-popover"
            onClick={(e: any) => setAnchorEl(e.currentTarget)}
          >
            <Icon glyph="tag" width={14} height={14} />
            <Spacer x={0.3} /> {t('add_tag')}
          </Button>
        </div>
      ) : null}
      <Popover
        id="tag-popover"
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setTagName('');
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuContainer>
          <div className="header">
            <Input
              startAdornment={
                <div style={{ paddingLeft: '8px' }}>
                  <Icon glyph="search" width={18} height={18} />
                </div>
              }
              placeholder={`${t('forms.placeholders.search')}`}
              autoFocus
              defaultValue={tagName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTagName(e.target.value.trim());
                if (e.target.value.trim().length) debouncedSearch();
              }}
              maxLength={90}
              onClick={(e: any) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              showCount={false}
            />
          </div>
          {!isTagsFetching &&
          !isTagsLoading &&
          tagName.length >= MIN_TAG_LENGTH ? (
            <div className="menu-content">
              {isTagValid(tagName.trim()) &&
              !filteredTags.find(
                (item: TagType) =>
                  item.name.toLowerCase() === tagName.toLowerCase(),
              ) ? (
                <div
                  className="menu-item"
                  onClick={() => createTag(tagName.trim())}
                >
                  {`${t('create')} '${tagName}'`}
                </div>
              ) : !isTagValid(tagName.trim()) ? (
                <div className="default-content error">
                  {t('forms.err_msg.contain')}
                </div>
              ) : null}
              {filteredTags?.map((opt: TagType) => (
                <div
                  className="menu-item"
                  key={opt.id}
                  onClick={() => addSelectedTags(opt)}
                >
                  {opt.name || '--'}
                </div>
              ))}
            </div>
          ) : (
            <div className="menu-content">
              <div className="default-content">
                {isTagsFetching
                  ? `${t('loading_tags')}`
                  : `${t('forms.placeholders.search_char')}`}
              </div>
            </div>
          )}
        </MenuContainer>
      </Popover>
      {!isBulkAction && responseData && (
        <TagList responseData={responseData} removeTag={removeTag} />
      )}
    </TagsContainer>
  );
};

export default ResponseTags;

type TagListType = {
  responseData: SurveyResponseType;
  removeTag: (tagId: string) => void;
};

const TagList = (props: TagListType) => {
  const { responseData, removeTag } = props;
  const { tags = [], surveyserv_response_id } = responseData;
  const {
    data: tagData = [],
    isLoading: isTagLoading,
    refetch,
  } = useGetTagInfo(tags || [], surveyserv_response_id);

  useEffect(() => {
    refetch();
  }, [tags]);

  if (tags?.length)
    return (
      <>
        {isTagLoading ? (
          <Skeleton
            width={128}
            height={28}
            count={2}
            style={{ margin: '0 5px' }}
            containerClassName="default-loader"
          />
        ) : (
          <>
            {tagData?.map((tag: TagType) => (
              <React.Fragment key={tag.id}>
                {tag?.name ? (
                  <div key={tag.id} className="selected-tag">
                    <Tag onCloseAction={() => removeTag(tag.id)}>
                      {tag.name}
                    </Tag>
                  </div>
                ) : null}
              </React.Fragment>
            ))}
          </>
        )}
      </>
    );
  return <></>;
};
